import { slugify } from '@/utils/slugify';
import { useCms } from '@/hooks/useCms';
import { useNavigate, useParams } from 'react-router-dom';
import { trackCustomEvent } from '@/lib/appInsights';
import { TrackingCustomEvents } from '@/constants/constants';
import { useIsAuthenticated } from '@azure/msal-react';
import { useUser } from '@/state/UserContext';
import type { CMSContextType } from '@/types/util.types';

interface ArticleTypeFilterProps {
  className?: string;
  disabled?: boolean;
}

function ArticleTypeFilter({ className, disabled }: ArticleTypeFilterProps) {
  const params = useParams();
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  const { email, userId } = useUser();
  const { contentLabels } = useCms() as CMSContextType;
  const typeSlug = params.contentLabel;
  const allOption = { id: 0, name: 'All' };

  function setTypeFilter(typeName: string) {
    const categorySlug = params.category;
    const subCategorySlug = params.subCategory || 'all';

    trackCustomEvent(TrackingCustomEvents.click.category.contentLabelFilter, {
      category: params.category ?? 'all',
      sub_category: params.subCategory ?? 'all',
      content_label: params.contentLabel ?? 'all',
      user_auth_status: isAuthenticated ? 'logged_in' : 'anonymous',
      user_email: email ?? 'anonymous',
      user_id: userId ?? 'anonymous',
    });

    if (typeName.toLowerCase() === 'all') {
      return navigate(`/category/${categorySlug}/${subCategorySlug}`);
    } else {
      const newType = slugify(typeName);
      return navigate(`/category/${categorySlug}/${subCategorySlug}/${newType}`);
    }
  }

  return (
    <div
      data-cy='article-type-filter'
      className={`px-2 md:px-3 overflow-y-hidden lg:px-4 no-scrollbar md:mt-3 overflow-x-scroll h-[40px] border-b border-[#D0D0D0] flex items-center ${className ?? ''}`}
    >
      <span className='mr-5 md:mr-6 mb-1 font-semibold text-gray-500'>Type:</span>
      <ol className='flex items-center gap-4 md:gap-6'>
        {[allOption, ...contentLabels].map((option) => {
          const active = typeSlug === slugify(option.name) || (option.id === 0 && !typeSlug);

          return (
            <li key={option.id}>
              <button
                disabled={disabled}
                onClick={() => setTypeFilter(option.name)}
                className={`py-1.5 min-w-[max-content] border-transparent border-b-[3px] text-[1.125rem] transition-all h-full duration-400 ${active ? '!text-[#7AD817] font-bold !border-[#7AD817]' : ''} ${disabled ? 'cursor-not-allowed opacity-50' : ''} ${!active && !disabled ? 'hover:text-teal-600 hover:border-teal-600 ' : ''}`}
              >
                {option.name}
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default ArticleTypeFilter;
